import React, { Component } from 'react';
import { Context } from 'context';

import Mousetrap from 'mousetrap';
import * as Sentry from '@sentry/browser';

import TitleBar from 'editor/workspace/window/titlebar/TitleBar';
import Viewer from "editor/workspace/window/viewer/Viewer";

import TableFactory from "util/TableFactory";

import styles from 'editor/workspace/window/window.module.scss';
import Loading from "components/loading/Loading";
import {COPY} from "text/errors";
import {axisLetter} from "util/mapModel";
import {parsePaste} from "util/paste";

class Window extends Component {
    static contextType = Context;

    window = React.createRef();

    state = {
        favourited: false,
        valueType: "modified", // Original, Uploaded or Modified
        diffType: "actual", // Actual, Relative or Percentage
        viewMode: "sheet", // Sheet, Both, Visual
        selection: {
            topLeft: {
                i: undefined,
                j: undefined
            },
            bottomRight: {
                i: undefined,
                j: undefined
            }
        },
        selectionFinalised: false,
        revision: 0,
        numberPad: false,
        recommendedLimWarningShown: false,
        axisAscendingWarningShown: false
    };

    async componentDidMount() {
        this.bindKeys(this.props.focused)
        if (this.props.window.axisEditingAxisName) this.setState({
            viewMode: "both",
        });
    }

    componentDidUpdate(prevProps) {
        if(prevProps.focused !== this.props.focused){
            this.bindKeys(this.props.focused)
        }
    }

    /**
     * Sends errors to sentry with map metadata
     * @param error
     */
    handleError = (error) => {
        Sentry.withScope(scope => {
            if (this.props.map.description) scope.setExtra("map_description", this.props.map.description);
            scope.setExtra("map_id", this.props.map.id);
            Sentry.captureException(error);
            console.debug(error);
        });
    };

    /**
     * Helper function that enforces recommended and physical map limits
     * @param table
     * @returns {Promise<void>}
     */
    enforceLimits = async (table) => {
        const dataAxis = table.dataAxis;
        let recommendedLimWarningShown = this.state.recommendedLimWarningShown;

        const withinRecommendedMin = dataAxis.min === null || dataAxis.min === dataAxis.minLimit ? true : dataAxis.matrix.every(x => x >= dataAxis.min);
        const withinRecommendedMax = dataAxis.max === null || dataAxis.max === dataAxis.maxLimit ? true : dataAxis.matrix.every(x => x <= dataAxis.max);

        if ((!withinRecommendedMin || !withinRecommendedMax) && !recommendedLimWarningShown && !this.props.map.swModified){
            const message = "Some values are beyond the recommended limits for this map. Do you wish to continue?";
            const wishToContinue = await this.context.confirm(message, "danger");

            if (!wishToContinue) {
                table.dataAxis.matrix = dataAxis.matrix.limit(dataAxis.min, dataAxis.max);
            }
            recommendedLimWarningShown = true;
        }

        const withinPhysicalMin = dataAxis.minLimit === null ? true : dataAxis.matrix.every(x => x >= dataAxis.minLimit);
        const withinPhysicalMax = dataAxis.maxLimit === null ? true : dataAxis.matrix.every(x => x <= dataAxis.maxLimit);

        if (!withinPhysicalMin || !withinPhysicalMax){
            table.dataAxis.matrix = dataAxis.matrix.limit(dataAxis.minLimit, dataAxis.maxLimit);
            this.context.alert("Some values were beyond the physical limits for this map. These have been automatically limited.", "neutral");
        }

        this.setState({ modified: table, revision: this.state.revision + 1, recommendedLimWarningShown});
    };

    /**
     * Send the table or axis changes to the api and then re-request the map data
     * @param table
     * @param {function(): void} [onCommitted] - called once the values of `table` are in the map's own table, which is before the request is asked for
     *   and long before it settles: the request can wait in the map's queue through a back-off, the hold after a lost answer and an open question.
     *   A gradient drag shows a copy of the table until then. It is also called, once, when this returns or throws without having committed.
     */
    save = async (table, onCommitted) => {
        let committed = false;
        const commit = () => {
            if (committed) return;

            committed = true;
            if (onCommitted) onCommitted();
        };

        try {
            await this.enforceLimits(table)

            if(!this.props.window.axisEditingAxisName){
                // The values that are saved are the values of the map's own table: that is what the window shows once the request is out, what a save that
                // fails leaves marked Not saved, and what Ok sends again. An edit made in place is in that table already. A gradient drag edits a copy of the
                // table (Viewer's preview) and hands the copy over, so its values go into the map's table here, before the request.
                this.props.map.modified.dataAxis.matrix = table.dataAxis.matrix;
                commit();
                await this.context.setMapModified(this.props.window.mapId, table.dataAxis.matrix.values);
            } else {
                const inOrder = table.dataAxis.matrix.values[0].every((_, j, axis) => {return j===0 ? true : axis[j-1] < axis[j]});
                if (!inOrder && !this.state.axisAscendingWarningShown) {
                    this.context.alert(`Axis values should usually be in ascending order`, "danger");
                    this.setState({axisAscendingWarningShown: true})
                }
                // The axis has the drag trap too: the axis that is edited may be a copy, so its values go into the map's own axis before the request, which
                // sends the axis on screen when it runs. The wire names the axis by its letter, not by the id of the table.
                const axisName = this.props.window.axisEditingAxisName;
                this.props.map.modified[axisName].matrix = table.dataAxis.matrix;
                commit();
                await this.context.setMapModifiedAxis(this.props.window.mapId, axisLetter(axisName), table.dataAxis.matrix.values[0])
            }
        } finally {
            commit();
        }
    };

    /**
     * Take any two corners of a selection, calculate TL + BR, and set them to state
     * @param start
     * @param end
     */
    setSelection = (start, end) => {
        const matrix = this.props.map.modified.dataAxis.matrix;
        this.setState({
            selection: matrix.selection(start, end),
            selectionFinalised: false,
        })
    };

    endSelection = () => {
        this.setState({
            selectionFinalised: true
        });
    };

    /**
     * Copy the selected data of this window into clipboard
     */
    copy = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if(this.state.selection.topLeft.i !== undefined){
            const modifiedTable = this.props.map.modified;
            const selectedMatrix = modifiedTable.dataAxis.matrix.extract(this.state.selection.topLeft, this.state.selection.bottomRight);
            await navigator.clipboard.writeText(selectedMatrix.toExcelString());
        }
    };

    /**
     * Paste from the clipboard into this window at the currently selected area. Text that holds a cell that is not a plain decimal number (util/paste.js)
     * is refused with a toast, before the table is changed and before anything is sent.
     */
    paste = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        let modifiedTable = this.props.map.modified;
        const topLeft = this.state.selection.topLeft;

        const clipboard = await navigator.clipboard.readText();

        if(this.state.valueType === "modified" && clipboard && (topLeft.i !== undefined)){
            const pasted = parsePaste(clipboard, modifiedTable.dataAxis.matrix.precision);
            if (!pasted.ok) {
                this.context.alert(COPY.pastedNotNumber, "danger");
                return;
            }

            modifiedTable.dataAxis.matrix = modifiedTable.dataAxis.matrix.insert(pasted.matrix, this.state.selection.topLeft);
            await this.save(modifiedTable);
        }
    };

    /**
     * Calculate and set the new selection on arrow key press
     * @param {number} x
     * @param {number} y
     */
    moveSelection = (x, y) => {
        let topLeft = this.state.selection.topLeft;
        let bottomRight = this.state.selection.bottomRight;

        let shape = {
            m: this.props.map.modified.dataAxis.matrix.shape.m,
            n: this.props.map.modified.dataAxis.matrix.shape.n
        };

        if(this.props.window.axisEditingAxisName) shape = {
            m: this.props.map.modified[this.props.window.axisEditingAxisName].matrix.shape.m,
            n: this.props.map.modified[this.props.window.axisEditingAxisName].matrix.shape.m
        };

        const overflowTop = topLeft.i + y < 0;
        const overflowLeft = topLeft.j + x < 0;
        const overflowBottom = bottomRight.i + y > (shape.m - 1);
        const overflowRight = bottomRight.j + x > (shape.n - 1);

        if(topLeft.i !== undefined && !overflowTop && !overflowLeft && !overflowBottom && !overflowRight){
            this.setSelection(
                {i: topLeft.i + y, j: topLeft.j + x},
                {i: bottomRight.i + y, j: bottomRight.j + x});
        }
    };

    /**
     * Bind or unbind key presses depending on whether this is the top <ProjectTicket /> in the stack
     * @param {boolean} focused
     */
    bindKeys = (focused) => {
        if(focused){
            Mousetrap.bind(this.context.text.shortcuts.copy.keys, this.copy);
            Mousetrap.bind(this.context.text.shortcuts.paste.keys, this.paste);
            Mousetrap.bind(this.context.text.shortcuts.moveSelectionUp.keys, this.moveSelection.bind(this, 0, -1));
            Mousetrap.bind(this.context.text.shortcuts.moveSelectionDown.keys, this.moveSelection.bind(this, 0, 1));
            Mousetrap.bind(this.context.text.shortcuts.moveSelectionRight.keys, this.moveSelection.bind(this, 1, 0));
            Mousetrap.bind(this.context.text.shortcuts.moveSelectionLeft.keys, this.moveSelection.bind(this, -1, 0));
            Mousetrap.bind(this.context.text.shortcuts.undo.keys, this.undo.bind(this));
            Mousetrap.bind(this.context.text.shortcuts.selectAll.keys, this.selectAll);
            Mousetrap.bind(this.context.text.shortcuts.revertChanges.keys, this.revertChanges)
        } else {
            Mousetrap.unbind(this.context.text.shortcuts.copy.keys);
            Mousetrap.unbind(this.context.text.shortcuts.paste.keys);
            Mousetrap.unbind(this.context.text.shortcuts.moveSelectionUp.keys);
            Mousetrap.unbind(this.context.text.shortcuts.moveSelectionDown.keys);
            Mousetrap.unbind(this.context.text.shortcuts.moveSelectionRight.keys);
            Mousetrap.unbind(this.context.text.shortcuts.moveSelectionLeft.keys);
            Mousetrap.unbind(this.context.text.shortcuts.undo.keys);
            Mousetrap.unbind(this.context.text.shortcuts.selectAll.keys);
            Mousetrap.unbind(this.context.text.shortcuts.revertChanges.keys);
        }
    };

    selectAll = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const start = {
            i: 0,
            j: 0
        };
        const end = {
            i: this.props.map.modified.dataAxis.matrix.shape.m - 1,
            j: this.props.map.modified.dataAxis.matrix.shape.n - 1
        };
        this.setSelection(start, end);
    };

    /**
     * Update state on input/select change (used by titlebar.js)
     * @param {Object} event
     */
    updateState = (event) => {
        //todo: tidy this
        this.setState({
            [event.target.name]: event.target.value
        }, () => {
            if(event.target.name === "viewMode"){
                setTimeout(() => {
                    this.props.handleResize();
                }, 1)
            }
        });
    };

    /**
     * Revert this map to its uploaded state: the map's reset (mapActions.resetMap), a step of the map's queue that puts the uploaded values on screen,
     * sends what the API does not hold one request after another, and says itself what went wrong. In axis mode only the axis that is edited is reset.
     */
    revertChanges = async () => {
        try {
            await this.context.resetMap(this.props.window.mapId, this.props.window.axisEditingAxisName ?? null);
        } catch (error) {
            this.handleError(error);
        }
    };

    undo = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            await this.context.undo(this.props.window.mapId)
        } catch (error) {
            this.handleError(error);
            this.context.alert("Cannot undo", "danger");
        }
    };

    /**
     * Bring this window to front if not already at front. Called on click
     */
    setMapSelected = (e) => {
        e.stopPropagation()
        if(!this.props.focused && e.target.alt !== "Close") {
            this.context.setSelectedWindow(this.props.window.mapId)
        }
    };

    close = (id) => {
        this.context.closeWindow(id);
    };

    /**
     * Activate axis editing, select the appropriate axis data and reset selection
     * @param {Object} axisName
     */
    activateAxisEditing = (axisName) => {
        const newState = {
            viewMode: "both",
        };

        if (!this.props.window.axisEditingAxisName){ // to stop deselection after every axis edit
            newState.selection = {
                topLeft: {
                    i: undefined,
                        j: undefined
                },
                bottomRight: {
                    i: undefined,
                        j: undefined
                }
            }
        }

        this.context.activateAxisEditing(this.props.window.mapId, axisName);

        this.setState(newState)
    };

    /**
     * Deactivate axis editing and reset selection
     */
    deactivateAxisEditing = () => {
        this.context.deactivateAxisEditing(this.props.window.mapId);
        this.setState({
            viewMode: "sheet",
            selection: {
                topLeft: {
                    i: undefined,
                    j: undefined
                },
                bottomRight: {
                    i: undefined,
                    j: undefined
                }
            }
        })
    };

    /**
     * Favourite the map
     */
    toggleMapFavourited = async () => {
        await this.context.toggleMapFavourited(this.props.window.mapId)
    };

    render() {
        let table = this.props.map?.[this.state.valueType];
        let uploaded = this.props.map?.uploaded;
        let modified = this.props.map?.modified;

        if(table && this.props.window.axisEditingAxisName) {
            table = table[this.props.window.axisEditingAxisName]
            uploaded = uploaded[this.props.window.axisEditingAxisName]
            modified = modified[this.props.window.axisEditingAxisName]

            table = {
                id: table.id,
                dataAxis: table,
                dimension: 1
            };
            uploaded = {
                id: uploaded.id,
                dataAxis: uploaded,
                dimension: 1
            };
            modified = {
                id: modified.id,
                dataAxis: modified,
                dimension: 1
            };
        }

        return (
            <div
                className={`${styles.window} ${this.props.focused ? 'highlightSecondary' : "highlightTertiary"} ${!table && styles.loading} bgSecondary`}
                onClick={this.setMapSelected}
                ref={this.window}
            >
                {table ?
                    <>
                        <TitleBar
                            id={this.props.window.mapId}
                            close={this.close}
                            table={table}
                            uploaded={uploaded}
                            map={this.props.map}
                            valueType={this.state.valueType}
                            diffType={this.state.diffType}
                            viewMode={this.state.viewMode}
                            updateWindow={this.updateState}
                            revertChanges={this.revertChanges}
                            axisEditingAxisName={this.props.window.axisEditingAxisName}
                            toggleMapFavourited={this.toggleMapFavourited}
                            deactivateAxisEditing={this.deactivateAxisEditing}
                            undo={this.undo}
                            focused={this.props.focused}
                            updateHexToFrom={this.props.updateHexToFrom}
                        />
                        <Viewer
                            table={table}
                            uploaded={uploaded}
                            modified={modified}
                            window={this.props.window}
                            diffType={this.state.diffType}
                            valueType={this.state.valueType}
                            viewMode={this.state.viewMode}
                            selection={this.state.selection}
                            selectionFinalised={this.state.selectionFinalised}
                            revision={this.state.revision}
                            save={this.save}
                            activateAxisEditing={this.activateAxisEditing}
                            selectAll={this.selectAll}
                            setSelection={this.setSelection}
                            endSelection={this.endSelection}
                            activateNumberPad={this.activateNumberPad}
                            closeNumberPad={this.closeNumberPad}
                            handleError={this.handleError}
                            numberPad={this.state.numberPad}
                            focused={this.props.focused}
                        />
                    </>
                    :
                    <Loading/>
                }
            </div>
        )
    }
}

export default Window;
